import { useEffect, useMemo, useState } from 'react';

import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Trash2 } from 'lucide-react';
import styled from 'styled-components';

import {
    TotadminPermission,
    useDeletePermissionGroupMutation,
    useGetPermissionCatalogQuery,
    useGetPermissionGroupQuery,
    useGetUsersByGroupsQuery,
    useUpdatePermissionGroupMutation,
} from 'data/__generated__';

import { PAGES } from 'constants/pages';
import { useHasPermission } from 'hooks/useHasPermission';
import { useUnsavedChangesPrompt } from 'hooks/useUnsavedChangesPrompt';

import { confirmPopup } from 'components/Popups/Confirm';
import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';
import { PermissionGrid } from 'components/PermissionGrid';
import { isGridEntry } from 'components/PermissionGrid/permissionGrid';
import { SectionContainer } from 'components/DetailsView/Section/SectionContainer';
import { TotemInput } from 'components/TotemInput';

import { PERMISSION_GROUP_APP_LABELS } from '../constants/permissionGroupApps';

type ParamTypes = {
    slug: string;
};

const sameSet = (a: readonly string[], b: readonly string[]): boolean =>
    a.length === b.length && a.every((code) => b.includes(code));

export const PermissionGroupDetails = () => {
    const { slug = '' } = useParams<ParamTypes>();
    const navigate = useNavigate();
    const canManagePermissions = useHasPermission(TotadminPermission.PermissionsManage);

    const { loading, data, error } = useGetPermissionGroupQuery({ variables: { slug } });
    const group = data?.permissionGroup;

    // Le catalogue COMPLET, pas celui de l'app du groupe : Toteam porte à la fois des droits fins et des
    // features historiques, et on veut libeller les deux.
    const { data: catalogData, loading: catalogLoading } = useGetPermissionCatalogQuery();
    const { data: membersData } = useGetUsersByGroupsQuery({ variables: { userGroups: [slug] } });

    const [updateGroup, { loading: updateLoading }] = useUpdatePermissionGroupMutation();
    const [deleteGroup, { loading: deleteLoading }] = useDeletePermissionGroupMutation({
        refetchQueries: ['GET_PERMISSION_GROUPS'],
    });

    const [label, setLabel] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [permissions, setPermissions] = useState<string[]>([]);

    useEffect(() => {
        if (group) {
            setLabel(group.label);
            setDescription(group.description);
            setPermissions(group.permissions);
        }
    }, [group]);

    const catalog = useMemo(() => catalogData?.permissionCatalog ?? [], [catalogData]);
    const gridCatalog = useMemo(() => catalog.filter(isGridEntry), [catalog]);
    const legacyLabels = useMemo(() => {
        const labelsByCode = new Map(catalog.map((entry) => [entry.code, entry.label]));
        const gridCodes = new Set(gridCatalog.map(({ code }) => code));

        return (group?.permissions ?? [])
            .filter((code) => !gridCodes.has(code))
            .map((code) => labelsByCode.get(code) ?? code)
            .sort((a, b) => a.localeCompare(b));
    }, [catalog, gridCatalog, group]);

    const isEditable = !!group?.isEditable && canManagePermissions;
    const isDirty =
        !!group &&
        (label !== group.label || description !== group.description || !sameSet(permissions, group.permissions));

    useUnsavedChangesPrompt({
        when: isDirty,
        message: 'Les droits modifiés sur ce groupe ne sont pas enregistrés.',
    });

    if (loading || catalogLoading) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <Loader />
            </div>
        );
    }

    if (!group || error) {
        throw new Error('Une erreur est survenue lors de la récupération du groupe de permissions');
    }

    const members = membersData?.usersByGroups ?? [];

    const handleSave = async () => {
        try {
            const { data: result } = await updateGroup({
                variables: { slug, input: { label, description, permissions } },
            });
            const payload = result?.updatePermissionGroup;

            if (!payload?.success) {
                toast.error(payload?.error ?? 'Une erreur est survenue lors de la mise à jour du groupe');
                return;
            }

            toast.success('Groupe mis à jour');
        } catch (mutationError) {
            toast.error(mutationError instanceof Error ? mutationError.message : 'Une erreur est survenue');
        }
    };

    const handleDelete = async () => {
        const confirmed = await confirmPopup({
            title: 'Supprimer le groupe',
            message: `Le groupe « ${group.label} » sera supprimé. Impossible s'il est encore affecté à des utilisateurs.`,
            isDestructive: true,
        });
        if (!confirmed) return;

        try {
            const { data: result } = await deleteGroup({ variables: { slug } });
            const payload = result?.deletePermissionGroup;

            if (!payload?.success) {
                toast.error(payload?.error ?? 'Une erreur est survenue lors de la suppression du groupe');
                return;
            }

            toast.success('Groupe supprimé');
            navigate(PAGES.permissionGroups.url);
        } catch (mutationError) {
            toast.error(mutationError instanceof Error ? mutationError.message : 'Une erreur est survenue');
        }
    };

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: PAGES.permissionGroups.url }],
        },
        ...(isEditable
            ? [
                  {
                      label: 'Actions',
                      items: [
                          {
                              label: 'Supprimer le groupe',
                              icon: <Trash2 />,
                              onClick: handleDelete,
                              variant: 'destructive' as const,
                              dataTest: 'permission-group-delete',
                          },
                      ],
                  },
              ]
            : []),
    ];

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.permissionGroupDetails} />
                </HeaderTitle>
                <ActionsAndSave
                    actionGroups={actionGroups}
                    onSave={handleSave}
                    saveLoading={updateLoading || deleteLoading}
                    saveDisabled={!isEditable || !isDirty || !label.trim()}
                    saveDataTest="permission-group-save"
                />
            </Header>
            <Content>
                {!group.isEditable ? (
                    <Notice>
                        Ce groupe est déclaré dans le code : il se consulte ici mais ne se modifie pas. Pour un profil
                        sur mesure, créez un groupe et composez-le.
                    </Notice>
                ) : null}
                <SectionContainer title="Groupe" isInitiallyOpen>
                    <Fields>
                        <TotemInput
                            label="Nom du groupe"
                            onChange={setLabel}
                            value={label}
                            disabled={!isEditable}
                            dataTest="permission-group-label"
                        />
                        <TotemInput
                            label="Description"
                            onChange={setDescription}
                            value={description}
                            disabled={!isEditable}
                        />
                        <Meta>
                            Identifiant <code>{group.slug}</code> · Application {PERMISSION_GROUP_APP_LABELS[group.app]}
                        </Meta>
                    </Fields>
                </SectionContainer>
                {gridCatalog.length > 0 && (group.isEditable || permissions.some((code) => isFine(code))) ? (
                    <SectionContainer title="Droits du back-office" isInitiallyOpen>
                        <PermissionGrid
                            catalog={gridCatalog}
                            value={permissions}
                            onChange={setPermissions}
                            disabled={!isEditable}
                        />
                    </SectionContainer>
                ) : null}
                {legacyLabels.length > 0 ? (
                    <SectionContainer title="Droits historiques (applications mobiles, webapp)" isInitiallyOpen={false}>
                        <LegacyList>
                            {legacyLabels.map((legacyLabel) => (
                                <li key={legacyLabel}>{legacyLabel}</li>
                            ))}
                        </LegacyList>
                    </SectionContainer>
                ) : null}
                <SectionContainer title={`Membres (${members.length})`} isInitiallyOpen>
                    {members.length ? (
                        <MemberList>
                            {members.map((member) => (
                                <li key={member._id}>
                                    <Link to={`/user/${member._id}`}>{member.profile.fullname || member.username}</Link>
                                </li>
                            ))}
                        </MemberList>
                    ) : (
                        <Meta>Aucun utilisateur ne porte ce groupe.</Meta>
                    )}
                </SectionContainer>
            </Content>
        </Container>
    );
};

const FINE_PERMISSIONS = new Set<string>(Object.values(TotadminPermission));
const isFine = (code: string): boolean => FINE_PERMISSIONS.has(code);

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    overflow-y: auto;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    padding: 15px;
    gap: 10px;
`;

const Fields = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding: 10px 0;
`;

const Meta = styled.p`
    font-size: 14px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const Notice = styled.div`
    padding: 10px 12px;
    border-radius: ${({ theme }) => theme.borderRadius};
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    color: ${({ theme }) => theme.textColor};
    font-size: 14px;
`;

const LegacyList = styled.ul`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 6px 20px;
    padding: 10px 0;
    font-size: 14px;
    color: ${({ theme }) => theme.textColor};
`;

const MemberList = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 0;
    font-size: 14px;

    a {
        color: ${({ theme }) => theme.textColor};
        text-decoration: underline;
    }
`;
